
import React, { useMemo } from "react";
import { LineChart, lineClasses } from "@mui/x-charts/LineChart";
import { useLanguage } from "../../Translations/LenguageContext";

const ENGINE_COLORS = {
    claude: "#da6843",
    gemini: "#5a74e5",
    perplexity: "#22a2ae",
    chatgpt: "var(--originalBlack)",
};

const LineChartStatus = ({
    fromDate,
    toDate,
    data = [],
    lineStyle
}) => {
    const {t} = useLanguage()
    const chartData = useMemo(() => {
        if (!fromDate || !toDate || fromDate > toDate) {
            return [];
        }

        const allDates = [];
        const current = new Date(`${fromDate}T12:00:00`);
        const end = new Date(`${toDate}T12:00:00`);

        while (current <= end) {
            const year = current.getFullYear();
            const month = String(
                current.getMonth() + 1
            ).padStart(2, "0");
            const day = String(
                current.getDate()
            ).padStart(2, "0");

            allDates.push(`${year}-${month}-${day}`);
            current.setDate(current.getDate() + 1);
        }

        const recordsByDate = {};

        (Array.isArray(data) ? data : []).forEach((item) => {
            if (!item.created_at || !item.engine) return;

            const parsedDate = new Date(item.created_at);

            if (Number.isNaN(parsedDate.getTime())) return;

            const date = parsedDate.toISOString().slice(0, 10);

            if (date < fromDate || date > toDate) return;

            const metricKey = Object.keys(item).find(
                (key) =>
                    ![
                        "created_at",
                        "engine",
                        "prompt_id",
                    ].includes(key)
            );

            const rawValue = metricKey
                ? item[metricKey]
                : null;

            const value =
                rawValue !== null &&
                    rawValue !== undefined &&
                    rawValue !== "" &&
                    Number.isFinite(Number(rawValue))
                    ? Number(rawValue)
                    : null;

            if (!recordsByDate[date]) {
                recordsByDate[date] = [];
            }

            recordsByDate[date].push({
                engine: item.engine.toLowerCase(),
                value,
            });
        });

        return allDates.map((date) => ({
            day: date,
            ...Object.fromEntries(
                Object.keys(ENGINE_COLORS).map((engine) => {
                    const records = (recordsByDate[date] || []).filter(
                        (item) => item.engine === engine
                    );

                    const values = records
                        .map((item) => item.value)
                        .filter((value) => value !== null);

                    const value = values.length
                        ? values.reduce(
                            (sum, currentValue) => sum + currentValue,
                            0
                        ) / values.length
                        : null;

                    return [engine, value];
                })
            ),
        }));
    }, [data, fromDate, toDate]);

    const chartDataWithZeroEdges = useMemo(() => {
        const enginesWithData = Object.keys(ENGINE_COLORS).filter(
            (engine) =>
                chartData.some((day) => day[engine] !== null)
        );

        return chartData.map((day, index) => {
            const updatedDay = { ...day };

            enginesWithData.forEach((engine) => {
                const firstIndex = chartData.findIndex(
                    (item) => item[engine] !== null
                );

                const lastIndex = chartData.findLastIndex(
                    (item) => item[engine] !== null
                );

                if (index < firstIndex) {
                    updatedDay[engine] = 0;
                }

                else if (index > lastIndex) {
                    updatedDay[engine] = null;
                }

                else if (day[engine] === null) {
                    updatedDay[engine] = 0;
                }
            });

            return updatedDay;
        });
    }, [chartData]);

    const chartSeries = useMemo(
        () =>
            Object.entries(ENGINE_COLORS)
                .filter(([engine]) =>
                    chartDataWithZeroEdges.some(
                        (day) => day[engine] !== null
                    )
                )
                .map(([engine, color]) => ({
                    id: engine,
                    dataKey: engine,
                    label:
                        engine.charAt(0).toUpperCase() +
                        engine.slice(1),
                    color,
                    curve: lineStyle,
                    showMark: true,
                    shape: "circle",
                    connectNulls: false,
                    valueFormatter: (value) =>
                        value == null
                            ? "No data"
                            : String(Number(value.toFixed(2))),
                })),
        [chartDataWithZeroEdges, lineStyle]
    );

    if (!fromDate || !toDate || fromDate > toDate) {
        return (
            <div className="empty_chart fade-in">
                <p>{t.geo.select_valid_date}</p>
            </div>
        );
    }

    if (chartSeries.length === 0) {
        return (
            <div className="empty_chart fade-in">
                <p>{t.geo.no_data_available_for_params}</p>
                <div style={{ display: "none" }}>
                </div>
            </div>
        );
    }

    return (
        <LineChart
            dataset={chartDataWithZeroEdges}
            className="fade-in"
            xAxis={[
                {
                    dataKey: "day",
                    scaleType: "point",
                    valueFormatter: (value) =>
                        new Date(
                            `${value}T12:00:00`
                        ).toLocaleDateString(
                            undefined,
                            {
                                day: "2-digit",
                                month: "short",
                            }
                        ),
                    tickLabelStyle: {
                        fill: "var(--originalBlack)",
                        fontSize: "10px"
                    },
                    axisLine: {
                        stroke: "var(--originalBlack)",
                    },
                    tickLine: {
                        stroke: "var(--originalBlack)",
                    },
                },
            ]}
            yAxis={[
                {
                    min: -2,
                    max: 10,
                    disableLine: true,
                    disableTicks: true,
                    width: 30,
                    tickLabelStyle: {
                        fill: "var(--originalBlack)",
                        fontSize: "10px"
                    },
                },
            ]}
            series={chartSeries}
            grid={{
                vertical: false,
                horizontal: true,
            }}
            sx={{
                "& .MuiChartsGrid-line": {
                    stroke: "var(--originalLightgray)",
                    strokeDasharray: "3 3",
                },
                [`& .${lineClasses.mark}`]: {
                    r: 2,
                },
            }}
            slotProps={{
                legend: {
                    hidden: true,
                },
                axisLine: {
                    hidden: true,
                },

                tooltip: {
                    sx: {
                        backgroundColor:
                            "var(--originalWhite)",
                        border:
                            "1px solid var(--originalLightgray)",
                        borderRadius: "10px",
                        padding: "5px 10px",
                        color:
                            "var(--originalBlack) !important",
                        "& *": {
                            backgroundColor:
                                "transparent !important",
                            border:
                                "1px solid transparent !important",
                            fontSize: "12px !important",
                            color:
                                "var(--originalBlack) !important",
                        },
                    },
                },
            }}
        />
    );
};

export default LineChartStatus;
